import { useEffect, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styled from 'styled-components';
import { ChipButton, PanelHeader, PanelHint, PanelTitle, SectionLabel, Segmented, SegmentButton, Swatch } from './ui/Panel';
import { ui } from './ui/tokens';

export type ToolsFillProps = {
	p5: () => any; // p5 instance
	/** Fill colour is shared with the pencil */
	pencilHex: string;
	backgroundHex: string;
	onEditColour: () => void;
	onEditBackground: () => void;
};

const FILL_MODE_HELP =
	'Connected fills only the touching area of the same colour you tap. ' +
	'Whole image replaces that colour everywhere on the canvas. ' +
	'Tip: to fill inside a shape, make sure its outline is fully closed — otherwise the fill spreads out.';

function ToolsFill({ p5, pencilHex, backgroundHex, onEditColour, onEditBackground }: ToolsFillProps) {
	const [isGlobal, setIsGlobal] = useState(false);

	useEffect(() => {
		if (typeof p5()?.getFillGlobal === 'function') setIsGlobal(!!p5().getFillGlobal());
	}, [p5]);

	const selectFillMode = (global: boolean) => {
		setIsGlobal(global);
		p5().setFillGlobal(global);
	};

	return (
		<div style={{ marginTop: 18 }}>
			<PanelHeader>
				<div>
					<PanelTitle>Bucket fill</PanelTitle>
					<PanelHint>Tap an area to fill it</PanelHint>
				</div>
				<ChipButton type="button" onClick={onEditColour}>
					<Swatch $color={pencilHex} />
					Colour
				</ChipButton>
			</PanelHeader>

			<SectionLabel>
				Fill mode
				<Tooltip title={FILL_MODE_HELP} enterTouchDelay={0} leaveTouchDelay={6000} arrow>
					<InfoOutlinedIcon sx={{ fontSize: 14, color: ui.label }} />
				</Tooltip>
			</SectionLabel>
			<Segmented>
				<SegmentButton type="button" active={!isGlobal} onClick={() => selectFillMode(false)}>
					Connected
				</SegmentButton>
				<SegmentButton type="button" active={isGlobal} onClick={() => selectFillMode(true)}>
					Whole image
				</SegmentButton>
			</Segmented>

			<Divider />

			<PanelHeader>
				<div>
					<PanelTitle>Canvas background</PanelTitle>
					<PanelHint>The colour behind your pixels</PanelHint>
				</div>
				<ChipButton type="button" onClick={onEditBackground}>
					<Swatch $color={backgroundHex} />
					Change
				</ChipButton>
			</PanelHeader>
		</div>
	);
}

export default ToolsFill;

const Divider = styled.div`
	height: 1px;
	background-color: ${ui.divider};
	margin: 14px 0;
`;
